// Chat-log message renderer for Home v2. Renders a `ChatVM` — the single shape
// produced by BOTH persisted thread_messages and the live streaming turn — so
// history and the in-flight reply look identical. The bold Geist/electric
// aesthetic is deferred to Phase 5; this builds on the existing shadcn tokens
// with restrained, reduced-motion-safe entrance motion (emil-design-eng) and
// full-border cards, never side-stripes (impeccable).

import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown, Flag, Image as ImageIcon, Pause, Play } from 'lucide-react'

import { Receipt } from '@/components/receipts/Receipt'
import type { ReceiptVM } from '@/lib/receipts'
import { threadKeys, type ChatVM, type UserVoiceVM } from '@/lib/threads'

// Internal bookkeeping tools the mechanic shouldn't see as chips.
const HIDDEN_TOOLS = new Set(['updateWorkingMemory', 'update_working_memory'])

const TOOL_LABELS: Record<string, string> = {
  lookup_customer: 'Looked up customer',
  create_customer: 'Added customer',
  update_customer: 'Updated customer',
  lookup_vehicle: 'Looked up vehicle',
  create_vehicle: 'Added vehicle',
  update_vehicle: 'Updated vehicle',
  decode_vin: 'Decoded VIN',
  list_appointments: 'Checked schedule',
  create_appointment: 'Booked appointment',
  update_appointment: 'Updated appointment',
  draft_estimate: 'Drafted estimate',
  update_vehicle_notes: 'Updated notes',
  tag_customer: 'Tagged customer',
  tag_vehicle: 'Tagged vehicle',
  undo_last_action: 'Undid last action',
  shop_stats: 'Checked shop stats',
}

function toolLabel(name: string): string {
  return TOOL_LABELS[name] ?? name.replace(/_/g, ' ')
}

const enterMotion =
  'animate-in fade-in slide-in-from-bottom-2 duration-200 ease-out motion-reduce:animate-none'

function UserBubble({
  text,
  imageUrl,
  hasImage,
  voice,
}: {
  text: string
  imageUrl?: string
  hasImage?: boolean
  voice?: UserVoiceVM
}) {
  const { t } = useTranslation('dashboard')
  if (voice) {
    return (
      <div className={`flex justify-end ${enterMotion}`}>
        <VoiceBubble text={text} voice={voice} />
      </div>
    )
  }
  return (
    <div className={`flex justify-end ${enterMotion}`}>
      <div className="flex max-w-[85%] flex-col items-end gap-1.5">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={t('home_v2.photo_attached')}
            className="max-h-40 rounded-2xl rounded-br-md border border-border object-cover"
          />
        ) : hasImage ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-hv-ink/10 px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
            <ImageIcon className="size-3" />
            {t('home_v2.photo_attached')}
          </span>
        ) : null}
        {text ? (
          <div className="whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-hv-ink px-3.5 py-2 text-[15px] leading-relaxed text-white">
            {text}
          </div>
        ) : null}
      </div>
    </div>
  )
}

// Deterministic pseudo-waveform heights (px) for a clip. Purely decorative —
// seeded from the message id so re-renders and reloads draw the same shape.
function waveHeights(seed: string, bars: number): number[] {
  let x = 0
  for (let i = 0; i < seed.length; i += 1) x = (x * 31 + seed.charCodeAt(i)) | 0
  const out: number[] = []
  for (let i = 0; i < bars; i += 1) {
    x = (x * 1103515245 + 12345) & 0x7fffffff
    out.push(6 + (x % 14))
  }
  return out
}

function formatClipSec(totalSec: number): string {
  const mins = Math.floor(totalSec / 60)
  const secs = Math.floor(totalSec % 60)
  return `${mins}:${String(secs).padStart(2, '0')}`
}

const VOICE_BARS = 24

// The voice-originated user message: replayable player + collapsed transcript.
// Same-session bubbles play the local recording (`blob:` URL, no fetch);
// reloaded rows fetch through the authed proxy (native <audio src> can't carry
// an Authorization header, so the bytes are fetched once into an object URL —
// clips are ≤ 25s / ≤ 2 MB, comfortable in memory).
//
// iOS Safari refuses HTMLMediaElement.play() outside the tap's synchronous
// call stack — an `await` (or even a requestAnimationFrame) between the tap
// and play() rejects with NotAllowedError. So the proxy fetch happens at
// MOUNT (a thread page holds ≤ 20 messages; clips are tens of KB), the
// <audio> element is always in the DOM, and the tap handler calls play()
// synchronously. A tap that races a still-loading fetch parks a pending-play
// flag; the post-fetch attempt is best-effort (Safari may refuse it — the
// clip is then loaded and the next tap plays).
function VoiceBubble({ text, voice }: { text: string; voice: UserVoiceVM }) {
  const { t } = useTranslation('voice_messages')
  const { getToken } = useAuth()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  // Object URLs this component minted (proxy fetches) get revoked on unmount;
  // a same-session `blob:` URL is owned by ThreadChat and left alone.
  const mintedRef = useRef<string | null>(null)
  const [src, setSrc] = useState<string | null>(
    voice.audioUrl.startsWith('blob:') ? voice.audioUrl : null,
  )
  const [error, setError] = useState<'expired' | 'failed' | null>(null)
  const [playing, setPlaying] = useState(false)
  const [progressSec, setProgressSec] = useState(0)
  const [transcriptOpen, setTranscriptOpen] = useState(false)
  // The user tapped play while the proxy fetch was still in flight. State
  // drives the spinner; the ref carries the intent into the post-commit
  // effect (reading state there would need a setState-in-effect to clear).
  const [pendingPlay, setPendingPlay] = useState(false)
  const pendingPlayRef = useRef(false)
  // In-flight proxy fetch — shared so a tap during the mount prefetch (or a
  // double-tap) reuses the same promise instead of minting a second URL.
  const fetchRef = useRef<Promise<string | null> | null>(null)

  const unmountedRef = useRef(false)
  useEffect(() => {
    // Reset on setup — StrictMode dev runs setup→cleanup→setup, and a
    // stuck-true flag would make every proxy fetch revoke its own URL.
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
      if (mintedRef.current) URL.revokeObjectURL(mintedRef.current)
    }
  }, [])

  const heights = waveHeights(voice.voiceMessageId ?? voice.audioUrl, VOICE_BARS)
  const durationSec = Math.max(1, Math.round(voice.durationMs / 1000))
  const playedBars = playing || progressSec > 0
    ? Math.min(VOICE_BARS, Math.round((progressSec / durationSec) * VOICE_BARS))
    : 0

  const ensureSrc = (): Promise<string | null> => {
    if (src) return Promise.resolve(src)
    fetchRef.current ??= (async () => {
      setError(null)
      try {
        const token = await getToken()
        if (!token) {
          setError('failed')
          return null
        }
        const res = await fetch(voice.audioUrl, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (res.status === 410) {
          setError('expired')
          return null
        }
        if (!res.ok) {
          setError('failed')
          return null
        }
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        if (unmountedRef.current) {
          // Unmounted while the proxy fetch was in flight — the cleanup effect
          // already ran with mintedRef empty, so revoke here or the URL leaks.
          URL.revokeObjectURL(url)
          return null
        }
        mintedRef.current = url
        setSrc(url)
        return url
      } catch {
        setError('failed')
        return null
      }
    })().then((url) => {
      // A null settle (error / expired / unmounted) clears the memo so a
      // later tap retries instead of replaying the failure forever.
      if (url === null) fetchRef.current = null
      return url
    })
    return fetchRef.current
  }

  // Fetch the clip at mount so a tap can play() synchronously (the iOS
  // constraint above). Same-session `blob:` bubbles already have src.
  useEffect(() => {
    if (!src) void ensureSrc()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only prefetch
  }, [])

  const onPlayPause = () => {
    const audio = audioRef.current
    if (audio && !audio.paused) {
      audio.pause()
      return
    }
    if (src && audio) {
      // Synchronous play() inside the tap's call stack — required on iOS.
      setError(null)
      audio.play().catch((err: unknown) => {
        console.warn('voice playback failed', err)
        setError('failed')
      })
      return
    }
    // A 410 is terminal — the R2 object is pruned; re-fetching flickers the
    // notice and can never succeed.
    if (error === 'expired') return
    // Fetch still in flight (or being retried after a failure) — park the
    // intent; the effect below attempts playback once React has committed the
    // new src. Playing HERE (before the commit) breaks: React's later
    // same-value src attribute write re-runs the media load algorithm and
    // rejects the in-flight play() with AbortError (both reviewers reproduced
    // this live).
    pendingPlayRef.current = true
    setPendingPlay(true)
    void ensureSrc().then((url) => {
      setPendingPlay(false)
      if (!url) pendingPlayRef.current = false
    })
  }

  // The parked tap, post-commit: the src attribute is now committed, so
  // play() can't be aborted by a later attribute write. Best-effort — iOS
  // refuses play() outside a tap's call stack (NotAllowedError → ignored; the
  // clip is loaded, the next tap plays synchronously); Android/desktop
  // autoplay here within the tap's transient activation. No setState in this
  // effect — the spinner clears in the fetch's .then.
  useEffect(() => {
    if (!src || !pendingPlayRef.current) return
    pendingPlayRef.current = false
    audioRef.current?.play().catch((err: unknown) => {
      if ((err as DOMException | null)?.name !== 'NotAllowedError') {
        console.warn('voice playback failed', err)
      }
    })
  }, [src])

  return (
    <div className="flex w-72 max-w-[85%] flex-col gap-1.5 rounded-2xl rounded-br-md bg-hv-ink px-3 py-2.5 text-white">
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={onPlayPause}
          aria-label={playing ? t('pause') : t('play')}
          title={playing ? t('pause') : t('play')}
          className="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-white/15 transition-colors hover:bg-white/25"
        >
          {pendingPlay ? (
            // Spinner only for a tap that outran the fetch — the mount
            // prefetch shouldn't flash a loader on every bubble.
            <span className="inline-block size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none" />
          ) : playing ? (
            <Pause className="size-4" />
          ) : (
            <Play className="size-4 translate-x-[1px]" />
          )}
        </button>

        <span aria-hidden className="flex h-6 flex-1 items-center gap-[2px] overflow-hidden">
          {heights.map((h, i) => (
            <span
              key={i}
              className={`w-[3px] rounded-sm ${i < playedBars ? 'bg-white' : 'bg-white/40'}`}
              style={{ height: `${h}px` }}
            />
          ))}
        </span>

        <span className="font-mono text-xs tabular-nums text-white/80">
          {formatClipSec(playing || progressSec > 0 ? progressSec : durationSec)}
        </span>
      </div>

      {voice.transcribing ? (
        <span className="flex items-center gap-1.5 text-xs text-white/70" aria-live="polite">
          <span className="inline-block size-3 animate-spin rounded-full border-2 border-current border-t-transparent motion-reduce:animate-none" />
          {t('transcribing')}
        </span>
      ) : text ? (
        <>
          <button
            type="button"
            onClick={() => setTranscriptOpen((v) => !v)}
            aria-expanded={transcriptOpen}
            className="flex min-h-11 items-center gap-1 self-start text-xs font-medium text-white/70 hover:text-white"
          >
            {t('transcript')}
            <ChevronDown
              className={`size-3 transition-transform ${transcriptOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {transcriptOpen ? (
            <p className="whitespace-pre-wrap break-words border-t border-white/15 pt-1.5 text-sm leading-relaxed text-white/90">
              {text}
            </p>
          ) : null}
        </>
      ) : null}

      {error ? (
        <p className="text-xs text-white/70">
          {error === 'expired' ? t('expired') : t('play_failed')}
        </p>
      ) : null}

      {/* Always mounted (src arrives async) so the tap handler can call
          play() on a live element synchronously — iOS requirement. */}
      <audio
        ref={audioRef}
        src={src ?? undefined}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false)
          setProgressSec(0)
        }}
        onTimeUpdate={(e) => setProgressSec(e.currentTarget.currentTime)}
        className="hidden"
      />
    </div>
  )
}

function EricBubble({ text, streaming }: { text: string; streaming?: boolean }) {
  const { t } = useTranslation('dashboard')
  return (
    <div className={`flex justify-start ${enterMotion}`}>
      <div className="flex max-w-[90%] gap-2.5">
        <span
          aria-hidden
          className="mt-1.5 size-2 shrink-0 rounded-full bg-hv-accent"
        />
        <div className="whitespace-pre-wrap break-words text-[15px] leading-relaxed text-foreground">
          {text}
          {streaming && text.length === 0 ? (
            <span className="text-muted-foreground">{t('home_v2.thinking')}</span>
          ) : null}
          {streaming && text.length > 0 ? (
            <span className="ml-0.5 inline-block h-4 w-1.5 translate-y-0.5 animate-pulse rounded-sm bg-muted-foreground/60 align-baseline motion-reduce:animate-none" />
          ) : null}
        </div>
      </div>
    </div>
  )
}

function ToolChip({ tool, detail }: { tool: string; detail?: string | null }) {
  if (HIDDEN_TOOLS.has(tool)) return null
  return (
    <div className={`flex justify-start ${enterMotion}`}>
      <span className="inline-flex items-center gap-1.5 rounded-full bg-hv-accent-soft px-2.5 py-0.5 text-xs font-medium text-hv-accent">
        <Check className="size-3" />
        {toolLabel(tool)}
        {detail ? <span className="font-normal opacity-70">· {detail}</span> : null}
      </span>
    </div>
  )
}

function SystemNote({ text }: { text: string }) {
  return (
    <div className={`flex justify-center ${enterMotion}`}>
      <span className="text-xs text-muted-foreground">{text}</span>
    </div>
  )
}

// A `report:` problem report Emil filed. Deliberately calm — this is a normal
// logging action, NOT an error — so it reads as a neutral note, not a red alert.
// Full-border card (never a side-stripe), visually distinct from Eric's reply,
// the user's pill, and the emerald result card so the transcript stays legible.
function ReportCard({ text }: { text: string }) {
  const { t } = useTranslation('dashboard')
  return (
    <div className={`flex justify-start ${enterMotion}`}>
      <div className="w-full max-w-[90%] rounded-xl border border-border bg-muted/40 p-3">
        <div className="flex items-center gap-2">
          <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <Flag className="size-3" />
          </span>
          <span className="text-xs font-medium text-muted-foreground">
            {t('home_v2.report_logged_label')}
          </span>
        </div>
        {text ? (
          <p className="mt-1.5 whitespace-pre-wrap break-words text-[15px] leading-relaxed text-foreground">
            {text}
          </p>
        ) : null}
      </div>
    </div>
  )
}

// Thin wrapper over the ONE receipt vocabulary (DESIGN.md §4.1, plan
// PR-6): the transcript's result rows render the same <Receipt> as the
// dock and Today — same undo pill, same silent §6 expiry, same
// mapUndoError reasons, same §9 telemetry. The ChatVM union is
// untouched; this only maps it.
function ResultCard({
  result,
  intent,
  requestId,
  undoable,
  undoneAt,
  createdAt,
}: Extract<ChatVM, { kind: 'result' }>) {
  const queryClient = useQueryClient()

  // Transcript rows can't yet distinguish the fast lane from an Eric
  // turn (the backend lane marker is the ticketed PR-7 follow-up), so
  // transcript receipts render in the assistant lane — the thread IS
  // the conversational surface.
  const vm: ReceiptVM = {
    // `undoable: false` means "nothing to undo" regardless of the
    // request id — map it to a null id so the Undo pill never shows.
    requestId: undoable ? requestId : null,
    lane: 'assistant',
    kind: intent || 'result',
    title: result.label,
    sub: null,
    url: result.url ?? null,
    createdAtIso: createdAt,
    undoneAt,
  }

  return (
    // No enterMotion here — <Receipt> owns its one-shot §7 rise, and the
    // dock convention (ReceiptsHost) is a plain wrapper. Stacking both
    // compounded the slide/fade on every mount.
    <div className="flex justify-start">
      <div className="w-full max-w-[90%]">
        <Receipt
          vm={vm}
          surface="thread"
          onUndone={() => {
            // Reflect the reversal across the reflex widgets + this thread.
            queryClient.invalidateQueries({ queryKey: ['agentic-load'] })
            queryClient.invalidateQueries({ queryKey: ['agentic-needs-action'] })
            queryClient.invalidateQueries({ queryKey: threadKeys.rail() })
          }}
        />
      </div>
    </div>
  )
}

export function ChatMessage({ vm }: { vm: ChatVM }) {
  switch (vm.kind) {
    case 'user':
      return (
        <UserBubble
          text={vm.text}
          imageUrl={vm.imageUrl}
          hasImage={vm.hasImage}
          voice={vm.voice}
        />
      )
    case 'eric':
      return <EricBubble text={vm.text} streaming={vm.streaming} />
    case 'tool':
      return <ToolChip tool={vm.tool} detail={vm.detail} />
    case 'system':
      return <SystemNote text={vm.text} />
    case 'report':
      return <ReportCard text={vm.text} />
    case 'result':
      return <ResultCard {...vm} />
    default:
      return null
  }
}
